import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "loadingOverlay",
    "exportContent",
    "checkbox",
    "startDate",
    "endDate",
    "employeeItem",
    "errorMessage",
    "errorText",
    "successMessage",
    "asOf",
    "option",
    "typeCheckbox",
  ]

  static values = {
    csvUrl: String,
    pdfUrl: String,
  }

  connect() {
    if (this.hasErrorTextTarget) {
      this.defaultErrorText = this.errorTextTarget.textContent
    }
    this.resetDialogState()
  }

  resetDialogState() {
    if (this.hasLoadingOverlayTarget) {
      this.loadingOverlayTarget.classList.add("hidden")
    }
    if (this.hasSuccessMessageTarget) {
      this.successMessageTarget.classList.add("hidden")
    }
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.classList.add("hidden")
    }
    if (this.hasExportContentTarget) {
      this.exportContentTarget.classList.remove("hidden", "opacity-50", "pointer-events-none")
    }
  }

  searchEmployees(event) {
    this.employeeQuery = event.target.value.toLowerCase().trim()
    this.filterEmployees()
  }

  // BEM dialog: employees who have left stay out of the picker until the option is ticked.
  toggleLeft(event) {
    this.showLeft = event.target.checked
    this.filterEmployees()
  }

  // The BEM picker arrives in a lazy frame after connect; filter each row as it lands.
  employeeItemTargetConnected(item) {
    this.filterEmployee(item)
  }

  // Mitarbeitertyp: ticking types hides the employees of other types and keeps them out of the
  // export (buildFormData). It never touches the checkboxes themselves, so picks made by hand are
  // still there once the types are unticked again.
  filterByType() {
    this.selectedTypes = new Set(
      this.typeCheckboxTargets
        .filter((checkbox) => checkbox.checked)
        .map((checkbox) => checkbox.value),
    )
    this.filterEmployees()
  }

  // Rows without a type marker (the BEM picker) always match.
  typeMatches(item) {
    if (!this.selectedTypes?.size || !item?.dataset.employeeTypeId) return true

    return this.selectedTypes.has(item.dataset.employeeTypeId)
  }

  filterEmployees() {
    this.employeeItemTargets.forEach((item) => this.filterEmployee(item))
  }

  filterEmployee(item) {
    const query = this.employeeQuery || ""
    const left = this.leftOnAsOf(item)
    item.querySelector("[data-left-marker]")?.classList.toggle("hidden", !left)
    const noMatch = query && !item.dataset.name.toLowerCase().includes(query)
    item.classList.toggle(
      "hidden",
      Boolean((left && !this.showLeft) || noMatch || !this.typeMatches(item)),
    )
  }

  // Mirrors Bem::CaseFinder.left?: deactivated or archived, or an exit date before the
  // Stichtag picked in the dialog (ISO dates compare as strings).
  leftOnAsOf(item) {
    if ("inactive" in item.dataset) return true

    const exitDate = item.dataset.exitDate
    const asOf = this.hasAsOfTarget && this.asOfTarget.value
    return Boolean(exitDate && asOf && exitDate < asOf)
  }

  selectAll(event) {
    event.preventDefault()
    // Never selects an employee the Mitarbeitertyp filter hides, nor drops one it hides.
    this.checkboxTargets.forEach((checkbox) => {
      if (this.typeMatches(checkbox.closest("[data-employee-type-id]"))) checkbox.checked = true
    })
  }

  deselectAll(event) {
    event.preventDefault()
    this.checkboxTargets.forEach((checkbox) => {
      checkbox.checked = false
    })
  }

  exportCsv(event) {
    event.preventDefault()
    this.performExport(this.csvUrlValue, "csv")
  }

  exportPdf(event) {
    event.preventDefault()
    this.performExport(this.pdfUrlValue, "pdf")
  }

  async performExport(baseUrl, format) {
    this.showLoading()
    this.hideError()
    this.hideSuccess()

    const formData = this.buildFormData(format)
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const response = await fetch(baseUrl, {
        method: "POST",
        headers: {
          "X-CSRF-Token": csrfToken,
          Accept: "application/json",
        },
        body: formData,
      })

      const json = await response.json()

      if (json.error === "no_data") {
        this.showError()
        return
      }

      if (json.success) {
        this.showSuccess()
        return
      }

      if (json.error) {
        console.error("Export failed:", json.error)
        this.showError(json.message)
        return
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
    } catch (error) {
      console.error("Export failed:", error)
      this.showError()
    } finally {
      this.hideLoading()
    }
  }

  buildFormData(format) {
    const formData = new FormData()

    if (format) {
      formData.append("format", format)
    }

    // BEM dialog: a single Stichtag plus named option checkboxes instead of a date range.
    if (this.hasAsOfTarget && this.asOfTarget.value) {
      formData.append("as_of", this.asOfTarget.value)
    }
    this.optionTargets.forEach((option) => {
      formData.append(option.name, option.checked ? "1" : "0")
    })

    if (this.hasStartDateTarget && this.startDateTarget.value) {
      formData.append("q[date_to_gteq]", this.startDateTarget.value)
    }
    if (this.hasEndDateTarget && this.endDateTarget.value) {
      formData.append("q[date_from_lteq]", this.endDateTarget.value)
    }

    // An empty selection is sent as such; the server refuses it rather than exporting everyone.
    this.selectedCheckboxes().forEach((cb) => {
      formData.append("q[employee_id_in][]", cb.value)
    })

    return formData
  }

  // Ticked employees the Mitarbeitertyp filter does not hide.
  selectedCheckboxes() {
    return this.checkboxTargets.filter(
      (checkbox) =>
        checkbox.checked && this.typeMatches(checkbox.closest("[data-employee-type-id]")),
    )
  }

  showLoading() {
    if (this.hasLoadingOverlayTarget) {
      this.loadingOverlayTarget.classList.remove("hidden")
    }
    if (this.hasExportContentTarget) {
      this.exportContentTarget.classList.add("opacity-50", "pointer-events-none")
    }
  }

  hideLoading() {
    if (this.hasLoadingOverlayTarget) {
      this.loadingOverlayTarget.classList.add("hidden")
    }
    if (this.hasExportContentTarget) {
      this.exportContentTarget.classList.remove("opacity-50", "pointer-events-none")
    }
  }

  showError(message) {
    if (this.hasErrorTextTarget) {
      this.errorTextTarget.textContent = message || this.defaultErrorText
    }
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.classList.remove("hidden")
    }
  }

  hideError() {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.classList.add("hidden")
    }
  }

  showSuccess() {
    if (this.hasSuccessMessageTarget) {
      this.successMessageTarget.classList.remove("hidden")
    }
    if (this.hasExportContentTarget) {
      this.exportContentTarget.classList.add("hidden")
    }
  }

  hideSuccess() {
    if (this.hasSuccessMessageTarget) {
      this.successMessageTarget.classList.add("hidden")
    }
    if (this.hasExportContentTarget) {
      this.exportContentTarget.classList.remove("hidden")
    }
  }
}
